export type DebugAccessRecord = {
  nonce: string
  address: string
  message: string
  createdAt: number
  expiresAt: number
  signature: string
}

export type DebugAccessMessageParams = {
  nonce: string
  origin: string
  address: string
  issuedAt: number
  appNetwork?: string
}

export const DEBUG_ACCESS_TTL_MS = 24 * 60 * 60 * 1000
export const DEBUG_ACCESS_STORAGE_KEY = 'truecurrent:debug-access'

export function normalizeDebugAddress(address?: string) {
  return address?.trim().toLowerCase() || ''
}

export function parseDebugAllowedAddresses(
  input?: string | ReadonlyArray<string>
) {
  const values = Array.isArray(input) ? input : String(input || '').split(',')

  return values
    .map(normalizeDebugAddress)
    .filter((address, index, addresses) => {
      return !!address && addresses.indexOf(address) === index
    })
}

export function isDebugAddressAllowed(params: {
  address?: string
  allowedAddresses: string[]
}) {
  const address = normalizeDebugAddress(params.address)

  if (!address) {
    return false
  }

  return params.allowedAddresses.includes(address)
}

export function createDebugAccessMessage(params: DebugAccessMessageParams) {
  const issuedAt = new Date(params.issuedAt).toISOString()

  return [
    'TrueCurrent Debug Access',
    `Address: ${params.address}`,
    `Origin: ${params.origin}`,
    `Network: ${params.appNetwork || 'unknown'}`,
    `Issued At: ${issuedAt}`,
    `Nonce: ${params.nonce}`,
    'This signature only unlocks local Labs tools for this browser session.'
  ].join('\n')
}

export function createDebugAccessRecord(params: {
  nonce: string
  ttlMs?: number
  address: string
  message: string
  createdAt: number
  signature: string
}) {
  return {
    nonce: params.nonce,
    message: params.message,
    createdAt: params.createdAt,
    signature: params.signature,
    address: normalizeDebugAddress(params.address),
    expiresAt: params.createdAt + (params.ttlMs ?? DEBUG_ACCESS_TTL_MS)
  }
}

export function isDebugAccessRecordValid(params: {
  now: number
  address?: string
  record?: null | DebugAccessRecord
}) {
  if (!params.record) {
    return false
  }

  if (params.record.expiresAt <= params.now) {
    return false
  }

  return params.record.address === normalizeDebugAddress(params.address)
}

export function readDebugAccessRecord(storage?: Storage) {
  if (!storage) {
    return null
  }

  try {
    const rawRecord = storage.getItem(DEBUG_ACCESS_STORAGE_KEY)

    return rawRecord ? (JSON.parse(rawRecord) as DebugAccessRecord) : null
  } catch {
    return null
  }
}

export function writeDebugAccessRecord(
  record: DebugAccessRecord,
  storage?: Storage
) {
  if (!storage) {
    return
  }

  storage.setItem(DEBUG_ACCESS_STORAGE_KEY, JSON.stringify(record))
}

export function clearDebugAccessRecord(storage?: Storage) {
  storage?.removeItem(DEBUG_ACCESS_STORAGE_KEY)
}
